/**
 * fb-model.ts — Flow builder constants, step factories, and tree helpers.
 * Ported verbatim from flow-builder-model.jsx + flow-builder-layout.jsx.
 *
 * 2026-08-31 — a decision's branches moved from `{ yes, no }` to the shared
 * ordered `paths` list in flow-paths.ts, so a third named branch is a data
 * change rather than a rewrite of every tree helper. Stored flows still carrying
 * the pair are migrated on read by `fbMigrateFlow`.
 */
import { T } from "@v0y4ge/jrni-ui";
import { t as i18nT } from "../../lib/i18n";
import { fpDefaultPaths, fpMigrate, fpLabel, fpMapSteps, FP_COMMON_LABELS, type FlowPath } from "./flow-paths";
export { fpLabel, fpStyle, fpAddPath, fpRemovePath, fpNextKey, FP_MAX_PATHS, FP_CUSTOM, type FlowPath } from "./flow-paths";

/** Labels offered when naming a decision's paths in a messaging or event flow.
 *  The journey builder reads its own per-type list off QFVocab. */
export const FB_PATH_LABELS = ["Responded", "No response", "Opened", "Didn't open", "Bounced", ...FP_COMMON_LABELS];

export const FB_NODE_W = 272;
export const FB_ACCENT = T.p1;
export const FB_GAP_X = 46, FB_SLOT = 50, FB_DGAP = 62, FB_ENDH = 34, FB_TRIG_H = 96;

export interface FBTrigger { id: string; label: string; labelKey: string; sub: string; }
export const FB_TRIGGERS: FBTrigger[] = [
  { id: "invited",    labelKey: "builder.fbModel.guestIsInvited", get label() { return i18nT("builder.fbModel.guestIsInvited"); },       sub: "Flow starts when a guest is added / invited" },
  { id: "registered", labelKey: "builder.fbModel.guestRegisters", get label() { return i18nT("builder.fbModel.guestRegisters"); },        sub: "Flow starts when a guest registers or RSVPs yes" },
  { id: "declined",   labelKey: "builder.fbModel.guestDeclines", get label() { return i18nT("builder.fbModel.guestDeclines"); },         sub: "Flow starts when a guest declines or RSVPs no" },
  { id: "waitlisted", labelKey: "builder.fbModel.guestJoinsWaitlist", get label() { return i18nT("builder.fbModel.guestJoinsWaitlist"); },   sub: "Flow starts when a guest is waitlisted" },
  { id: "published",  labelKey: "builder.fbModel.eventIsLive", get label() { return i18nT("builder.fbModel.eventIsLive"); },          sub: "Flow starts when the event goes live" },
  { id: "joined_queue", labelKey: "builder.fbModel.customerJoinsTheQueue", get label() { return i18nT("builder.fbModel.customerJoinsTheQueue", "Customer joins the queue"); }, sub: "Flow starts when a customer takes a place in a queue" },
  { id: "booked",     labelKey: "builder.fbModel.customerBooksAnAppointment", get label() { return i18nT("builder.fbModel.customerBooksAnAppointment", "Customer books an appointment"); }, sub: "Flow starts when an appointment is booked" },
  { id: "requested",  labelKey: "builder.fbModel.customerRequestsPrivateAccess", get label() { return i18nT("builder.fbModel.customerRequestsPrivateAccess", "Customer requests private access"); }, sub: "Flow starts when someone requests a private service or event" },
];
export const FB_TRIGGER = (id: string) => FB_TRIGGERS.find((t) => t.id === id) ?? FB_TRIGGERS[0];

export const FB_DECISION_CONDS = [
  "Registered within 2 days","Responded positively","Accepted the invitation",
  "Opened the message","Clicked the link","Confirmed attendance","Attended the event","Made a purchase",
  "Responded to the invitation","Attended the appointment","Confirmed the appointment",
  "Was served","Still in the queue","Got a seat (not waitlisted)","Approved",
];
export const FB_EXIT_OUTCOMES = ["Declined","Completed the flow","Goal met","No response","Expired","Unsubscribed","Bounced"];
export const FB_WAIT_UNITS = ["Minutes","Hours","Days"];
export const FB_WAIT_TIMINGS = [
  { value:"after_prev",    labelKey: "builder.fbModel.afterThePreviousStep", get label() { return i18nT("builder.fbModel.afterThePreviousStep"); } },
  { value:"before_event",  labelKey: "builder.fbModel.beforeTheEvent", get label() { return i18nT("builder.fbModel.beforeTheEvent"); } },
  { value:"after_event",   labelKey: "builder.fbModel.afterTheEvent", get label() { return i18nT("builder.fbModel.afterTheEvent"); } },
];
export const FB_SENDWHEN = ["Immediately","After previous step","Scheduled date"];

let _fbSeq = 0;
const fbId = (p: string) => `${p}_${Date.now().toString(36)}${(_fbSeq++).toString(36)}${Math.random().toString(36).slice(2,4)}`;

export interface StepComms  { id:string; kind:"comms";    messageId:string|null; sendWhen:string; landingId:string; }
export interface StepWait   { id:string; kind:"wait";     amount:number; unit:string; timing:string; }
/** evalAmount/evalUnit — the decision's evaluation window (flow-audit item 3):
 *  how long to wait for the condition before taking the fallback path.
 *  0/undefined = evaluate immediately.
 *
 *  `paths` is ordered: the first is the met path, the last is the fallback. Two
 *  entries render exactly as YES / NO always did. */
export interface StepDecision{ id:string; kind:"decision"; condition:string; paths:FlowPath<FBStep>[]; evalAmount?:number; evalUnit?:string; }
export interface StepLanding{ id:string; kind:"landing";  landingId:string; }
export interface StepSurvey { id:string; kind:"survey";   surveyId:string|null; sendWhen:string; }
export interface StepExit   { id:string; kind:"exit";     outcome:string; note:string; }
/** Join — the explicit merge marker (flow-audit item 2, option b): placed
 *  directly after a decision, both branches fall through into it and the
 *  trunk continues. Renders as a merge node; validation enforces placement. */
export interface StepJoin   { id:string; kind:"join"; }
export type FBStep = StepComms|StepWait|StepDecision|StepLanding|StepSurvey|StepExit|StepJoin;

export interface FBFlow { trigger:{ event:string }; steps:FBStep[]; }

export const fbMkComms   = (messageId:string|null=null): StepComms    => ({ id:fbId("c"), kind:"comms",    messageId, sendWhen:"Immediately", landingId:"" });
export const fbMkWait    = (): StepWait    => ({ id:fbId("w"), kind:"wait",    amount:1, unit:"Days", timing:"after_prev" });
export const fbMkDecision= (): StepDecision=> ({ id:fbId("d"), kind:"decision", condition:"Registered within 2 days", paths:fpDefaultPaths<FBStep>() });
export const fbMkLanding = (): StepLanding => ({ id:fbId("lp"), kind:"landing", landingId:"" });
export const fbMkSurvey  = (surveyId:string|null=null): StepSurvey => ({ id:fbId("sv"), kind:"survey", surveyId, sendWhen:"Immediately" });
export const fbMkExit    = (): StepExit    => ({ id:fbId("x"), kind:"exit",    outcome:"Declined", note:"" });
export const fbMkJoin    = (): StepJoin    => ({ id:fbId("j"), kind:"join" });

export function fbDefaultFlow(): FBFlow {
  return { trigger:{ event:"invited" }, steps:[fbMkComms(), fbMkDecision()] };
}

/** Read-time migration: every stored decision gains its `paths` list, whether it
 *  was stored as one or as the old `{ yes, no }` pair. Idempotent, so it is safe
 *  on every load. */
export function fbMigrateFlow(flow: FBFlow): FBFlow {
  const walk = (steps: FBStep[]): FBStep[] => (steps ?? []).map((s) => {
    if (s.kind !== "decision") return s;
    const paths = fpMigrate<FBStep>(s).map((p) => ({ ...p, steps: walk(p.steps) }));
    const { yes: _y, no: _n, ...rest } = s as unknown as Record<string, unknown>;
    return { ...(rest as unknown as StepDecision), paths };
  });
  return { trigger: flow?.trigger ?? { event: "invited" }, steps: walk(flow?.steps ?? []) };
}

export function fbFindStep(steps: FBStep[], id: string): FBStep|null {
  for (const s of steps) {
    if (s.id===id) return s;
    if (s.kind==="decision") {
      for (const p of (s.paths ?? [])) { const r = fbFindStep(p.steps ?? [],id); if (r) return r; }
    }
  }
  return null;
}

export function fbEdit(steps: FBStep[], id: string, fn: (s:FBStep)=>FBStep): FBStep[] {
  return steps.map((s) => {
    if (s.id===id) return fn(s);
    if (s.kind==="decision") return { ...s, paths:fpMapSteps(s.paths,(list)=>fbEdit(list,id,fn)) };
    return s;
  });
}

export function fbRemove(steps: FBStep[], id: string): FBStep[] {
  return steps.filter((s) => s.id!==id).map((s) =>
    s.kind==="decision" ? { ...s, paths:fpMapSteps(s.paths,(list)=>fbRemove(list,id)) } : s
  );
}

/** `pathKey` names which branch of `parentId` to insert into — null for the
 *  trunk. Was `branch: "yes"|"no"`. */
export interface InsertDesc { parentId:string|null; pathKey:string|null; index:number; }
export function fbInsert(steps: FBStep[], desc: InsertDesc, step: FBStep): FBStep[] {
  if (!desc.parentId) { const n = steps.slice(); n.splice(desc.index,0,step); return n; }
  return steps.map((s) => {
    if (s.kind!=="decision") return s;
    if (s.id===desc.parentId) {
      return { ...s, paths:(s.paths ?? []).map((p) => {
        if (p.key!==desc.pathKey) return p;
        const arr = (p.steps ?? []).slice();
        arr.splice(desc.index,0,step);
        return { ...p, steps:arr };
      }) };
    }
    return { ...s, paths:fpMapSteps(s.paths,(list)=>fbInsert(list,desc,step)) };
  });
}

/** Human label for a wait step — timing-aware so the card reads naturally.
 *  before_event → "7 days before the event"; after_event → "… after the event";
 *  after_prev (default) → "Wait 7 days". */
export function fbWaitLabel(s: StepWait): string {
  const unit = String(s.unit || "").toLowerCase();
  const qty = `${s.amount} ${unit}`;
  if (s.timing === "before_event") return `${qty} before the event`;
  if (s.timing === "after_event") return `${qty} after the event`;
  return `Wait ${qty}`;
}

export function fbNodeHeight(s: FBStep|null): number {
  if (!s) return 96;
  if (s.kind==="comms")    return 92 + (s.landingId?30:0);
  if (s.kind==="wait")     return 60;
  if (s.kind==="survey")   return 92;
  if (s.kind==="decision") return 80;
  if (s.kind==="landing")  return 90;
  if (s.kind==="exit")     return 86;
  if (s.kind==="join")     return 44;
  return 80;
}
export const fbIsTerminal = (s: FBStep|null) => !!s && (s.kind==="decision"||s.kind==="exit");

/* ── Validation (flow-audit item 2) — every path must end in an End flow ── */
export interface FBIssue { stepId: string|null; message: string; }

/** Walks the whole tree and returns structural problems: paths that never
 *  end, empty decision branches, unreachable steps after a decision or End,
 *  unattached messages/surveys, and misplaced Joins. `joined` = leaves of
 *  this list legitimately fall through to a Join at an enclosing level. */
export function fbValidate(flow: FBFlow): FBIssue[] {
  const issues: FBIssue[] = [];
  const walk = (steps: FBStep[], joined: boolean) => {
    steps.forEach((s, i) => {
      const isLast = i === steps.length - 1;
      if (s.kind === "comms" && !s.messageId) issues.push({ stepId: s.id, message: "No message attached — this step sends nothing" });
      if (s.kind === "survey" && !s.surveyId) issues.push({ stepId: s.id, message: "No survey attached — this step sends nothing" });
      if (s.kind === "join" && (i === 0 || steps[i - 1].kind !== "decision")) issues.push({ stepId: s.id, message: "Join must sit directly after a decision" });
      if (s.kind === "exit" && !isLast) issues.push({ stepId: s.id, message: "Steps after an End flow are unreachable" });
      if (s.kind === "decision") {
        const next = steps[i + 1];
        const hasJoin = !!next && next.kind === "join";
        if (next && !hasJoin) issues.push({ stepId: s.id, message: "Steps after a decision are unreachable — add a Join right after it, or move them into a branch" });
        const continues = hasJoin || (isLast && joined);
        /* One message per named path. Two literal messages about YES and NO
           could not name a third branch, and "Empty Otherwise branch" reads
           better than "Empty NO branch" ever did. */
        (s.paths ?? []).forEach((p) => {
          if (!(p.steps ?? []).length && !continues) issues.push({ stepId: s.id, message: `Empty ${fpLabel(p)} branch — add steps or an End flow` });
        });
        (s.paths ?? []).forEach((p) => walk(p.steps ?? [], continues));
      }
    });
    const last = steps[steps.length - 1];
    if (last && last.kind !== "exit" && last.kind !== "decision" && !joined) issues.push({ stepId: last.id, message: "This path never ends — finish it with an End flow step" });
  };
  if (!(flow.steps || []).length) issues.push({ stepId: null, message: "Flow has no steps — add at least one step and an End flow" });
  else walk(flow.steps, false);
  return issues;
}
